import { ContentItem } from '@betterfunnel/types';
import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import TextField from '@betterfunnel/ui-core/TextField';
import { Dialog } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import { SnackbarContext } from '../contexts/SnackbarContext';

interface UpdatableComparisonCategory {
  id: string;
  name?: string;
}

interface Props {
  item?: ContentItem;
  open: boolean;
  onClose: () => void;
  onSave: (item: ContentItem) => Promise<void> | void;
}

export default function CreateComparisonCategoryModal({
  item,
  open,
  onClose,
  onSave,
}: Props) {
  const { show } = useContext(SnackbarContext);

  const [data, setData] = useState<UpdatableComparisonCategory>({
    id: uid(),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    setData(item ? JSON.parse(JSON.stringify(item)) : { id: uid() });
    setError(undefined);
    setIsSaving(false);
  }, [open, item]);

  const createCategory = useCallback(async () => {
    setError(undefined);

    if (!data.name) {
      setError(new Error('Category name is required'));
      show('Category name is required');
      return;
    }

    const res = onSave(data as any);
    if (res && 'then' in res) {
      setIsSaving(true);
      res.catch((e) => {
        Sentry.captureException(e);
        setError(e);
      });
      res.finally(() => setIsSaving(false));
    }
  }, [data, onSave, show]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: isSaving,
        onClick: () => onClose(),
      },
      {
        title: item ? 'Update' : 'Create',
        type: 'primary',
        loading: isSaving,
        onClick: () => createCategory(),
      },
    ],
    [createCategory, isSaving, item, onClose],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions}>
      <Dialog.Title
        as="h3"
        className="text-base leading-6 font-semibold text-gray-900"
      >
        {item
          ? 'Update comparison category'
          : 'Create a new comparison category'}
      </Dialog.Title>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          title="Name"
          value={data.name}
          onChange={(e) => {
            setData((d) => {
              d.name = e.target.value;
              return { ...d };
            });
          }}
        />
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
